import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  AfterViewInit,
  ChangeDetectionStrategy,
  SimpleChanges,
} from "@angular/core";
import { FormControl } from "@angular/forms";
import { Observable } from "rxjs";
import { map, startWith } from "rxjs/operators";
import { ItemData } from "../../../core/models/common/multi-select-item-data.model";

@Component({
  standalone: false,
  selector: "app-multiselect-autocomplete",
  templateUrl: "./multiselect-autocomplete.component.html",
  styleUrls: ["./multiselect-autocomplete.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiselectAutocompleteComponent implements OnInit, AfterViewInit {
  @Input() myHint: string;
  /**
   * Event that is emitted when clicking on the filter
   */
  @Output() afterClicked = new EventEmitter<boolean>();
  /**
   * It will emit the values ​​that are selected
   */
  @Output() result = new EventEmitter<{ data: Array<any> }>();
  /**
   * At the end of listing the entities
   */
  @Output() elementsListed = new EventEmitter<boolean>();
  @Input() filterDisabled = false;
  @Input() placeholder: string = "Seleccione";
  /**
   * Internal value that will be taken when selecting
   */
  @Input() itemsSelected: Array<string> = [];
  @Input() data: Array<any> = [];
  @Input() valueToSelect: any = "id";
  @Input() returnFullValue;
  /**
   * Text to be displayed in the select
   */
  @Input() valueToSearch: any = "name";
  @Input() maxElementsToSelect;
  @Input() disabledMultiselect = false;
  @Input() clearTrigger = false;
  @Input() disableCondition?: (item: any) => boolean;
  @Input() panelWidth?: string | number;
  myPlaceholder;
  showText;
  selectControl = new FormControl();
  rawData: Array<ItemData> = [];
  selectData: Array<ItemData> = [];
  filteredData: Observable<Array<ItemData>>;
  filterString: string = "";
  results: Array<any> = [];
  lastFilteredData: any[] = [];
  myDisableWhenValue: any;
  constructor() {
    this.initValues();
  }

  ngOnInit(): void {
    this.filteredData.subscribe(data => {
      this.lastFilteredData = data || [];
    });
  }

  ngAfterViewInit() {
    this.showText = this.valueToSearch;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['clearTrigger'] && !changes['clearTrigger'].firstChange) {
      this.resetForm();
    }

    if (
      changes && changes['data'] &&
      changes['data'].currentValue &&
      changes['data'].currentValue.length > 0 &&
      (!changes['data'].previousValue ||
        changes['data'].previousValue.length == 0 ||
        this.isNotEqualsList(
          changes['data'].currentValue,
          changes['data'].previousValue
        ))
    ) {
      this.setDataValues();
    }

    if (
      changes && changes['data'] &&
      changes['data'].currentValue &&
      changes['data'].currentValue.length === 0 &&
      changes['data'].previousValue &&
      changes['data'].previousValue.length > 0
    ) {
      this.cleanValues();
    }

    if (
      changes && changes['placeholder'] &&
      changes['placeholder'].firstChange &&
      changes['placeholder'].currentValue
    ) {
      this.myPlaceholder = this.placeholder;
    }
  }

  isNotEqualsList(currentValue: any, previousValue: any) {
    return JSON.stringify(currentValue) != JSON.stringify(previousValue);
  }

  cleanValues() {
    this.rawData = [];
    this.initValues();
  }

  setDataValues() {
    this.cleanValues();
    this.data.forEach((item: any) => {
      this.rawData.push({ item, selected: false });
    });
    this.emitEntitiesListed();
    this.initValues();
  }

  initValues() {
    this.filteredData = this.selectControl.valueChanges.pipe(
      startWith<string>(""),
      map((value) => (typeof value === "string" ? value : this.filterString)),
      map((filter) => this.filter(filter))
    );
  }

  reset() {
    this.selectControl.setValue(null);
    this.initValues();
  }

  onAutocompleteClosed() {
    if (!this.lastFilteredData.length) return;

    if (this.selectControl.value != null) {
      this.reset();
    }
  }

  resetForm() {
    this.reset();
    this.results = [];
    this.rawData.map((itemData: ItemData) => {
      itemData.selected = false;
    });
    this.selectData = [];
    this.initilizePlaceholder();
  }

  showList() {
    if (!this.selectControl.value) {
      this.initValues();
    }
  }

  initilizePlaceholder() {
    this.myPlaceholder = this.placeholder;
  }

  setInfoValues() {
    if (this.results.length == 0) {
      this.initilizePlaceholder();
    }

    if (this.results.length > 0) {
      this.myPlaceholder =
        this.placeholder +
        " (" +
        this.results.length +
        (this.results.length == 1 ? " seleccionado)" : " seleccionados)");
    }
  }

  filter = (filter: string): Array<ItemData> => {
    this.filterString = filter;
    if (filter.length > 0) {
      return this.rawData.filter((option) => {
        return (
          option.item[this.valueToSearch]
            .toLowerCase()
            .indexOf(filter.toLowerCase()) >= 0
        );
      });
    } else {
      return this.rawData.slice();
    }
  };
  displayFn = (): string => "";

  /**
   * function used by the parent component to assign selected elements when using it in an edit
   */
  setValues(itemsSelected: any[], emitEntitySelectedEvent: boolean = true) {
    this.selectData = [];
    if (itemsSelected && itemsSelected.length > 0 && this.rawData.length > 0) {
      itemsSelected.map((itemSelected, index) => {
        let itemFounded = this.valueToSelect ? this.rawData.find(
          (element) => element.item[this.valueToSelect] === itemSelected
        ) : this.rawData.find(
          (element) => JSON.stringify(element.item) === JSON.stringify(itemSelected)
        );
        if (itemFounded) {
          const emitList = index == itemsSelected.length - 1;
          this.toggleSelection(itemFounded, emitList, emitEntitySelectedEvent);
        }
      });
    }
  }

  optionClicked(event: Event, data: ItemData) {
    if ((this.disabledMultiselect || this.filterDisabled) && !data.selected) {
      return;
    }
    event.stopPropagation();
    this.toggleSelection(data);
  }

  toggleSelection = (data: ItemData, emitList: boolean = true, emitEntitySelectedEvent: boolean = true): void => {
    data.selected = !data.selected;
    if (data.selected === true) {
      this.selectData.push(data);
    } else {
      const i = this.selectData.findIndex((value) => value.item === data.item);
      this.selectData.splice(i, 1);
    }
    if (emitList) {
      this.emitAdjustedData(emitEntitySelectedEvent);
    }
  };

  emitAdjustedData = (emitEntitySelectedEvent: boolean = true): void => {
    this.results = [];
    this.selectData.forEach((data: ItemData) => {
      this.results.push(data.item);
    });
    if (emitEntitySelectedEvent) {
      this.result.emit({
        data: this.returnFullValue
          ? this.results
          : this.results.map((data) => data[this.valueToSelect]),
      });
    }
    this.setInfoValues();
  };

  removeChip = (data: ItemData): void => {
    this.toggleSelection(data);
  };

  getItemList() {
    this.afterClicked.emit(true);
  }

  emitEntitiesListed() {
    this.elementsListed.emit(true);
  }

  hasHint() {
    return this.myHint && this.data.length == 0;
  }

  disableMultiSelect() {
    this.placeholder = '';
    this.selectControl.disable();
  }

  isChecboxDisabled(data: any): boolean {
    if (this.filterDisabled) {
      return true;
    }

    const isMaxReached =
      this.maxElementsToSelect &&
      this.results.length === this.maxElementsToSelect;

    const propertyCondition = this.disableCondition?.(data.item) ?? false;

    const shouldDisable =
      this.disabledMultiselect ||
      isMaxReached ||
      propertyCondition;

    return shouldDisable && !data.selected;
  }

  isOptionDisabled(data: any): boolean {
    if (this.filterDisabled) return true;

    const isMaxReached =
      this.maxElementsToSelect &&
      this.results.length === this.maxElementsToSelect;

    const propertyCondition = this.disableCondition?.(data.item) ?? false;

    const shouldDisable =
      this.disabledMultiselect ||
      isMaxReached ||
      propertyCondition;

    return shouldDisable && !data.selected;
  }
}
